import { useEffect, useRef, useState, useCallback } from "react";
import {
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from "@microsoft/signalr";
import { defaultUrls } from "../../constants/endpoints";

/**
 * @typedef {Object} MaintenanceStatus
 * @property {boolean} enabled              Whether a maintenance window is active.
 * @property {string} [tokensInvalidatedAt] ISO timestamp marking the window; the
 *                                          authoritative dedup key for a fresh window.
 */

// Fallback poll cadence. Only fires while the socket is NOT connected — it exists
// only to cover dropped sockets / blocked WebSockets / cold-start races, so it
// stays deliberately slow (see spec: do not lower below 60s).
const FALLBACK_POLL_MS = 60_000;

/**
 * Subscribes to maintenance state over SignalR with a slow polling fallback.
 *
 * Ordering of truth:
 *   1. Initial GET /maintenance/status on mount (SignalR keeps no history, so
 *      the socket alone can miss a window that was already open at load time).
 *   2. Live `MaintenanceChanged` pushes from `/hubs/maintenance`.
 *   3. A 60s fallback poll that runs ONLY while the connection is not Connected.
 *
 * `onActivated` fires at most once per distinct `tokensInvalidatedAt`, so rapid
 * double-toggles never re-trigger a logout for a window already handled.
 *
 * @param {(status: MaintenanceStatus) => void} [onActivated]
 * @returns {{ status: MaintenanceStatus|null, isMaintenance: boolean, connected: boolean }}
 */
export default function useMaintenanceStatus(onActivated) {
    const [status, setStatus] = useState(null);
    const [connected, setConnected] = useState(false);

    // Hold the latest callback in a ref so the effect below can run once (empty
    // deps) without re-opening the socket every time the parent re-renders.
    const onActivatedRef = useRef(onActivated);
    useEffect(() => {
        onActivatedRef.current = onActivated;
    }, [onActivated]);

    // Last window we already dispatched `onActivated` for. Guards the dedup.
    const lastFiredRef = useRef(null);

    const applyStatus = useCallback((next) => {
        if (!next) return;
        setStatus(next);

        // Only a genuinely-open window with a NEW timestamp triggers the callback.
        // `enabled` alone is not enough — a lingering window keeps `enabled` true
        // but must not re-fire logout (spec: always check tokensInvalidatedAt).
        const stamp = next.tokensInvalidatedAt;
        if (next.enabled && stamp && stamp !== lastFiredRef.current) {
            lastFiredRef.current = stamp;
            if (typeof onActivatedRef.current === "function") {
                onActivatedRef.current(next);
            }
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        let connection = null;
        let fallbackTimer = null;

        // Plain fetch (not the app axios instance) on purpose: the endpoint is
        // public/CORS-open and must work even after auth tokens are invalidated,
        // and it must NOT trip the axios 401 interceptor.
        const fetchStatus = async () => {
            try {
                const res = await fetch(defaultUrls.maintenanceStatus, {
                    method: "GET",
                    cache: "no-store",
                });
                if (!res.ok) return;
                const env = await res.json();
                // Response envelope: { success, result: MaintenanceStatus, ... }
                const result = env && (env.result || env.Result);
                if (isMounted && result) applyStatus(result);
            } catch (_e) {
                // Swallow — the socket or the next fallback tick will recover.
            }
        };

        const startFallbackPoll = () => {
            if (fallbackTimer) return;
            fallbackTimer = setInterval(() => {
                if (
                    connection &&
                    connection.state === HubConnectionState.Connected
                ) {
                    return; // socket is healthy — skip the poll.
                }
                fetchStatus();
            }, FALLBACK_POLL_MS);
        };

        const init = async () => {
            // 1. Seed initial state before the socket is up.
            await fetchStatus();

            // 2. Open the live subscription.
            connection = new HubConnectionBuilder()
                .withUrl(defaultUrls.maintenanceHub)
                .withAutomaticReconnect()
                .configureLogging(LogLevel.None)
                .build();

            // Message name is case-sensitive per the backend contract.
            connection.on("MaintenanceChanged", (s) => {
                if (isMounted) applyStatus(s);
            });

            const syncConnected = () => {
                if (isMounted) {
                    setConnected(
                        connection.state === HubConnectionState.Connected
                    );
                }
            };
            connection.onreconnecting(syncConnected);
            connection.onreconnected(() => {
                syncConnected();
                // Catch anything we missed while the socket was down.
                fetchStatus();
            });
            connection.onclose(syncConnected);

            try {
                await connection.start();
                syncConnected();
            } catch (_e) {
                // Connect failed (proxy blocks WS, cold start, etc.) — the
                // fallback poll below carries us until reconnect succeeds.
            }

            // 3. Fallback poll — self-gates on connection state.
            startFallbackPoll();
        };

        init();

        return () => {
            isMounted = false;
            if (fallbackTimer) clearInterval(fallbackTimer);
            if (connection) {
                // Detach handlers before stopping to avoid a late state update.
                connection.off("MaintenanceChanged");
                connection.stop();
            }
        };
    }, [applyStatus]);

    return {
        status,
        isMaintenance: !!(status && status.enabled),
        connected,
    };
}
